import { useEffect, useRef, useState } from "react";

export type RealtimeConnectionState = "connecting" | "connected" | "reconnecting" | "fallback";

export type RealtimeEvent = {
  version: number;
  event_id: string;
  type:
    | "message.created"
    | "neuralchat.message.created"
    | "mention.created"
    | "neuralchat.mention.created"
    | "room.created"
    | "neuralchat.room.created"
    | "neuralchat.read_cursor.updated"
    | "agent_job.created"
    | "neuralchat.agent_job.created"
    | "neuralchat.opportunity.created"
    | "neuralchat.opportunity.transitioned"
    | "neuralchat.project.created"
    | "neuralchat.project.revised"
    | "neuralchat.project.transitioned"
    | "neuralchat.work_item.created"
    | "neuralchat.work_item.transitioned"
    | "neuralchat.work_item.updated"
    | "neuralchat.work_item.dependency_added"
    | "neuralchat.work_item.dependency_removed"
    | "neuralchat.approval.requested"
    | "neuralchat.approval.decided"
    | "mail.message.received"
    | "mail.routing.conflict"
    | "mail.thread.bound"
    | "mail.draft.created"
    | "mail.draft.revised"
    | "mail.draft.cancelled"
    | "mail.oauth.succeeded";
  room_id?: string;
  message_id?: string;
  member_id?: string;
  agent_job_id?: string;
  opportunity_id?: string;
  project_id?: string;
  work_item_id?: string;
  approval_id?: string;
  revision_id?: string;
  snapshot_hash?: string;
  occurred_at: string;
  connection_id?: string;
  account_id?: string;
  thread_id?: string;
  draft_id?: string;
  conflict_id?: string;
  revision?: string;
  canonical_hash?: string;
};

type EventCallbacks = {
  onMessage?: (event: RealtimeEvent) => void;
  onMention?: (event: RealtimeEvent) => void;
  onRoom?: (event: RealtimeEvent) => void;
  onAgentJob?: (event: RealtimeEvent) => void;
  onProject?: (event: RealtimeEvent) => void;
  onMail?: (event: RealtimeEvent) => void;
  onAuthorizationLost?: (code: 4401 | 4403) => void;
};

const MAX_RECONNECT_DELAY = 30000;
export const isAuthorizationCloseCode = (code: number): code is 4401 | 4403 => code === 4401 || code === 4403;
const KNOWN_TYPES = new Set<RealtimeEvent["type"]>([
  "message.created",
  "neuralchat.message.created",
  "mention.created",
  "neuralchat.mention.created",
  "room.created",
  "neuralchat.room.created",
  "neuralchat.read_cursor.updated",
  "agent_job.created",
  "neuralchat.agent_job.created",
  "neuralchat.opportunity.created",
  "neuralchat.opportunity.transitioned",
  "neuralchat.project.created",
  "neuralchat.project.revised",
  "neuralchat.project.transitioned",
  "neuralchat.work_item.created",
  "neuralchat.work_item.transitioned",
  "neuralchat.work_item.updated",
  "neuralchat.work_item.dependency_added",
  "neuralchat.work_item.dependency_removed",
  "neuralchat.approval.requested",
  "neuralchat.approval.decided",
  "mail.message.received",
  "mail.routing.conflict",
  "mail.thread.bound",
  "mail.draft.created",
  "mail.draft.revised",
  "mail.draft.cancelled",
  "mail.oauth.succeeded",
]);
const POINTER_KEYS = new Set(["version", "event_id", "type", "room_id", "message_id", "member_id", "agent_job_id", "opportunity_id", "project_id", "work_item_id", "approval_id", "revision_id", "snapshot_hash", "occurred_at", "connection_id", "account_id", "thread_id", "draft_id", "conflict_id", "revision", "canonical_hash"]);

export const websocketUrl = (path: string) => {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}${path}`;
};

export const parseRealtimeEvent = (value: unknown): RealtimeEvent | null => {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  if (Object.keys(candidate).some((key) => !POINTER_KEYS.has(key))) return null;
  if (candidate.version !== 1 || typeof candidate.event_id !== "string" || !candidate.event_id || typeof candidate.type !== "string" || !KNOWN_TYPES.has(candidate.type as RealtimeEvent["type"]) || typeof candidate.occurred_at !== "string") return null;
  const event = candidate as unknown as RealtimeEvent;
  if (["message.created", "neuralchat.message.created", "mention.created", "neuralchat.mention.created"].includes(event.type) && (typeof event.room_id !== "string" || typeof event.message_id !== "string")) return null;
  if (["mention.created", "neuralchat.mention.created"].includes(event.type) && typeof event.member_id !== "string") return null;
  if (["room.created", "neuralchat.room.created"].includes(event.type) && typeof event.room_id !== "string") return null;
  if (event.type === "neuralchat.read_cursor.updated" && (typeof event.room_id !== "string" || typeof event.member_id !== "string" || typeof event.message_id !== "string")) return null;
  if (["agent_job.created", "neuralchat.agent_job.created"].includes(event.type) && (typeof event.room_id !== "string" || typeof event.message_id !== "string" || typeof event.agent_job_id !== "string")) return null;
  if (event.type.startsWith("neuralchat.opportunity.") && (typeof event.room_id !== "string" || typeof event.opportunity_id !== "string")) return null;
  if (event.type.startsWith("neuralchat.project.") && (typeof event.room_id !== "string" || typeof event.project_id !== "string")) return null;
  if (event.type.startsWith("neuralchat.work_item.") && (typeof event.room_id !== "string" || typeof event.project_id !== "string" || typeof event.work_item_id !== "string")) return null;
  if (event.type.startsWith("neuralchat.approval.") && (typeof event.room_id !== "string" || typeof event.project_id !== "string" || typeof event.approval_id !== "string")) return null;
  if (event.type.startsWith("mail.") && !(event.thread_id || event.draft_id || event.conflict_id || event.message_id || event.connection_id)) return null;
  return event;
};

const useSocketRealtime = (path: string | null, callbacks: EventCallbacks, expectedRoomId?: string) => {
  const [state, setState] = useState<RealtimeConnectionState>("fallback");
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  useEffect(() => {
    if (!path || typeof WebSocket === "undefined") {
      setState("fallback");
      return undefined;
    }
    let stopped = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | null = null;
    let heartbeatTimer: number | null = null;
    let attempt = 0;
    let authorizationStopped = false;
    const seen = new Set<string>();

    const clearTimers = () => {
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      if (heartbeatTimer !== null) window.clearInterval(heartbeatTimer);
      reconnectTimer = null;
      heartbeatTimer = null;
    };

    const scheduleReconnect = () => {
      if (stopped || authorizationStopped) return;
      setState("fallback");
      const base = Math.min(MAX_RECONNECT_DELAY, 1000 * (2 ** attempt));
      const jitter = 0.8 + Math.random() * 0.4;
      attempt += 1;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        setState("reconnecting");
        connect();
      }, Math.round(base * jitter));
    };

    const connect = () => {
      if (stopped) return;
      clearTimers();
      setState(attempt === 0 ? "connecting" : "reconnecting");
      socket = new WebSocket(websocketUrl(path));
      socket.onopen = () => {
        if (stopped) return;
        attempt = 0;
        setState("connected");
        heartbeatTimer = window.setInterval(() => {
          if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "ping" }));
        }, 30000);
      };
      socket.onmessage = (message) => {
        let decoded: unknown;
        try {
          decoded = JSON.parse(message.data);
        } catch {
          return;
        }
        const event = parseRealtimeEvent(decoded);
        if (!event || (expectedRoomId && event.room_id !== expectedRoomId) || seen.has(event.event_id)) return;
        seen.add(event.event_id);
        if (seen.size > 256) seen.delete(seen.values().next().value as string);
        if (["message.created", "neuralchat.message.created"].includes(event.type)) callbacksRef.current.onMessage?.(event);
        else if (["mention.created", "neuralchat.mention.created"].includes(event.type)) callbacksRef.current.onMention?.(event);
        else if (["room.created", "neuralchat.room.created"].includes(event.type)) callbacksRef.current.onRoom?.(event);
        else if (["agent_job.created", "neuralchat.agent_job.created"].includes(event.type)) callbacksRef.current.onAgentJob?.(event);
        else if (event.type.startsWith("neuralchat.opportunity.") || event.type.startsWith("neuralchat.project.") || event.type.startsWith("neuralchat.work_item.") || event.type.startsWith("neuralchat.approval.")) callbacksRef.current.onProject?.(event);
        else if (event.type.startsWith("mail.")) callbacksRef.current.onMail?.(event);
      };
      socket.onerror = () => { socket?.close(); };
      socket.onclose = (closeEvent) => {
        clearTimers();
        if (stopped) return;
        if (isAuthorizationCloseCode(closeEvent.code)) {
          authorizationStopped = true;
          setState("fallback");
          callbacksRef.current.onAuthorizationLost?.(closeEvent.code);
          return;
        }
        scheduleReconnect();
      };
    };

    connect();
    return () => {
      stopped = true;
      clearTimers();
      socket?.close();
      socket = null;
    };
  }, [path, expectedRoomId]);

  return state;
};

export const useNeuralChatRoomRealtime = (roomId: string | undefined, callbacks: EventCallbacks) =>
  useSocketRealtime(roomId ? `/ws/neuralchat/rooms/${encodeURIComponent(roomId)}/` : null, callbacks, roomId);

export const useNeuralChatNotificationRealtime = (enabled: boolean, callbacks: EventCallbacks) =>
  useSocketRealtime(enabled ? "/ws/neuralchat/notifications/" : null, callbacks);

export const useNeuralChatMailRealtime = (callbacks: EventCallbacks) =>
  useSocketRealtime("/ws/neuralchat/notifications/", callbacks);
